import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CBadge,
  CButton,
  CCardBody,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CForm,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCaretRight, cilCheck, cilSend, cilX } from "@coreui/icons";
import { apiClient } from "services/Api";
import {
  readSubmitButtonParams,
  writeSubmitButtonParams,
} from "../_utils/queryParams";

const DEFAULT_POLL_MS = 2000;

const SubmitApplication = ({
  id,
  taskId: initialTaskId,
  typology,
  pages = [],
  submission,
  setToast,
  className,
}) => {
  const mode = submission?.mode;
  const enabled = Boolean(submission?.enabled);

  const initialFromParams = readSubmitButtonParams();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [taskId, setTaskId] = useState(mode === "async" ? initialTaskId : null);
  const [status, setStatus] = useState(null);
  const [currentSection, setCurrentSection] = useState(-1);
  const [plannedSections, setPlannedSections] = useState([]);
  const [errorInfo, setErrorInfo] = useState(null);
  const [awaitingContinue, setAwaitingContinue] = useState(false);
  const [username, setUsername] = useState(initialFromParams.username);
  const [password, setPassword] = useState(initialFromParams.password);
  const [code, setCode] = useState(initialFromParams.code);
  const [gov, setGov] = useState(initialFromParams.gov);
  const [serverSeqVersion, setServerSeqVersion] = useState(0);

  const pollingRef = useRef(null);
  const serverSectionsRef = useRef([]);
  const sectionsListRef = useRef(null);
  const lastScrollTopRef = useRef(null);
  const sectionsInitializedRef = useRef(false);

  const pageCount = pages.length;

  useEffect(() => {
    writeSubmitButtonParams({ username, password, code, gov });
  }, [username, password, code, gov]);

  useEffect(() => {
    setTaskId(mode === "async" ? initialTaskId : null);
  }, [initialTaskId, mode]);

  useEffect(() => {
    setPlannedSections((prev) => {
      const nextAll = Array.from({ length: pageCount }, (_, index) => index);
      const filteredPrev = prev.filter((index) => index >= 0 && index < pageCount);

      if (!sectionsInitializedRef.current) {
        if (pageCount > 0) {
          sectionsInitializedRef.current = true;
          return nextAll;
        }
        return filteredPrev;
      }

      return filteredPrev;
    });
  }, [pageCount]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const updateServerSections = (sections) => {
    serverSectionsRef.current = Array.isArray(sections) ? sections : [];
    setServerSeqVersion((value) => value + 1);
  };

  const clearPolling = (keepLoading = false) => {
    setLoading(keepLoading);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const extractState = (data) => {
    const currentState = data?.current_state || data?.current_sate || null;
    return {
      sections: Array.isArray(currentState?.sections) ? currentState.sections : [],
      current:
        typeof currentState?.current_section === "number"
          ? currentState.current_section
          : null,
    };
  };

  const normalizeServerSections = (sections) => {
    const max = pageCount - 1;
    return sections.filter(
      (index) => Number.isInteger(index) && (index === -1 || (index >= 0 && index <= max)),
    );
  };

  const startPolling = (nextTaskId, interval = DEFAULT_POLL_MS) => {
    clearPolling(true);
    setTaskId(nextTaskId);
    setAwaitingContinue(false);

    pollingRef.current = setInterval(() => {
      apiClient
        .get(`/application/${typology}/${id}/task/${nextTaskId}/`)
        .then((res) => {
          const data = res?.data || {};

          if (data?.success === true) {
            setStatus("COMPLETE");
            setToast({
              title: "Candidatura",
              message: "Candidatura submetida com sucesso!",
              type: "success",
              autoClose: false,
            });
            setAwaitingContinue(true);
            clearPolling(false);
            return;
          }

          if (data?.success === false) {
            const nextError = { error: data?.error, type: data?.type };
            setErrorInfo(nextError);
            setStatus("ERROR");
            setToast({
              title: "Candidatura",
              message: `Erro na secção: ${nextError.error} (${nextError.type || "Erro"})`,
              type: "error",
              autoClose: false,
            });
            setAwaitingContinue(true);
            clearPolling(false);
            return;
          }

          if (res.status === 202 || data?.status === "PENDING") {
            const { sections, current } = extractState(data);
            const normalized = normalizeServerSections(sections);
            updateServerSections(normalized);
            if (typeof current === "number") {
              setCurrentSection(current);
            }
            setStatus("PENDING");
          }
        })
        .catch((error) => {
          const errData = error.response?.data || {};
          setErrorInfo({ error: errData.error, type: errData.type });
          setStatus("ERROR");
          setToast({
            title: "Candidatura",
            message: `Deu um erro na secção: ${errData.error || "Desconhecida"}`,
            type: "error",
            autoClose: false,
          });
          setAwaitingContinue(true);
          clearPolling(false);
        });
    }, interval);
  };

  useEffect(() => {
    if (mode !== "async" || !initialTaskId) {
      return undefined;
    }

    setLoading(true);
    startPolling(initialTaskId);
    return () => clearPolling();
  }, [initialTaskId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (lastScrollTopRef.current != null && sectionsListRef.current) {
      sectionsListRef.current.scrollTop = lastScrollTopRef.current;
      lastScrollTopRef.current = null;
    }
  }, [plannedSections]);

  const toggleVisibility = () => setVisible((value) => !value);

  const toggleAllSections = () => {
    if (sectionsListRef.current) {
      lastScrollTopRef.current = sectionsListRef.current.scrollTop;
    }

    const allIndexes = Array.from({ length: pageCount }, (_, index) => index);
    const nextValue = plannedSections.length === pageCount ? [] : allIndexes;
    setPlannedSections(nextValue);
  };

  const toggleSection = (index) => {
    if (sectionsListRef.current) {
      lastScrollTopRef.current = sectionsListRef.current.scrollTop;
    }

    setPlannedSections((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index].sort((a, b) => a - b),
    );
  };

  const handleSubmit = () => {
    const sectionsToSend = [...plannedSections].sort((a, b) => a - b);
    if (!sectionsToSend.length) {
      setToast({
        title: "Candidatura",
        message: "Selecione pelo menos uma secção.",
        type: "error",
      });
      return;
    }

    setLoading(true);
    setErrorInfo(null);
    setStatus(null);
    setAwaitingContinue(false);
    updateServerSections([]);
    setCurrentSection(-1);

    apiClient
      .post(`/application/${typology}/${id}/`, {
        username: username.trim(),
        password: password.trim(),
        code: code.trim(),
        gov,
        sections: sectionsToSend,
      })
      .then((res) => {
        const data = res?.data || {};

        if (mode === "async") {
          const nextTaskId = data?.task_id;
          const { sections, current } = extractState(data);
          updateServerSections(normalizeServerSections(sections));
          if (typeof current === "number") {
            setCurrentSection(current);
          }
          if (!nextTaskId) {
            throw new Error("Task ID not returned by server.");
          }
          startPolling(nextTaskId, DEFAULT_POLL_MS);
          return;
        }

        setStatus("COMPLETE");
        setLoading(false);
        setVisible(false);
        setToast({
          title: "Candidatura",
          message: data?.message || "Candidatura submetida com sucesso!",
          type: "success",
        });
      })
      .catch((error) => {
        const errData = error.response?.data || {};
        setErrorInfo({ error: errData.error, type: errData.type });
        setToast({
          title: "Candidatura",
          message:
            errData.error || "Ocorreu um erro a enviar candidatura para o formulário",
          type: "error",
          autoClose: false,
        });
        setLoading(false);
      });
  };

  const handleCancelTask = () => {
    if (!taskId) {
      clearPolling(false);
      setTaskId(null);
      setStatus(null);
      updateServerSections([]);
      setCurrentSection(-1);
      setErrorInfo(null);
      setAwaitingContinue(false);
      return;
    }

    apiClient
      .delete(`/application/${typology}/${id}/task/${taskId}/`)
      .then((res) => {
        if (res.status !== 204) {
          return;
        }

        clearPolling(false);
        setTaskId(null);
        setStatus(null);
        updateServerSections([]);
        setCurrentSection(-1);
        setErrorInfo(null);
        setAwaitingContinue(false);
        setToast({
          title: "Candidatura",
          message: "Submissão cancelada com sucesso.",
          type: "success",
        });
      })
      .catch(() => {
        setToast({
          title: "Candidatura",
          message: "Erro ao tentar cancelar a submissão.",
          type: "error",
        });
      });
  };

  const handleContinue = () => {
    setAwaitingContinue(false);
    setLoading(false);
    setVisible(false);
  };

  const progressInfo = useMemo(() => {
    const serverSections = serverSectionsRef.current;
    if (!serverSections.length) {
      return { total: 0, done: 0 };
    }
    const currentIndex = serverSections.indexOf(currentSection);
    const done = Math.max(0, currentIndex);
    return { total: serverSections.length, done };
  }, [currentSection, serverSeqVersion]);

  const sectionStatus = (index) => {
    const serverSections = serverSectionsRef.current;
    const inPlanned = plannedSections.includes(index);
    const inServer = serverSections.includes(index);

    if (index === -1) {
      if (currentSection === -1) {
        return { color: "info", label: "Em curso" };
      }
      return { color: "success", label: "Concluída" };
    }

    if (currentSection === index) {
      if (!inPlanned) {
        return { color: "info", label: "A ler" };
      }
      if (status === "COMPLETE") {
        return { color: "success", label: "Concluída" };
      }
      return { color: "info", label: "Em curso" };
    }

    if (inServer) {
      const position = serverSections.indexOf(index);
      const currentPosition = serverSections.indexOf(currentSection);

      if (!inPlanned) {
        if (position > -1 && currentPosition > -1 && position < currentPosition) {
          return { color: "warning", label: "Lido" };
        }
        return { color: "secondary", label: "Na fila" };
      }

      if (position > -1 && currentPosition > -1 && position < currentPosition) {
        return { color: "success", label: "Concluída" };
      }
      if (position > currentPosition) {
        return { color: "secondary", label: "Na fila" };
      }
    }

    if (inPlanned) {
      return { color: "secondary", label: "Planeada" };
    }
    return { color: "light", label: "—" };
  };

  const renderSections = () => {
    const serverSections = serverSectionsRef.current;
    const base = new Set([-1]);

    plannedSections.forEach((index) => base.add(index));
    serverSections.forEach((index) => base.add(index));

    if (typeof currentSection === "number") {
      base.add(currentSection);
    }

    const all = Array.from(base).sort((a, b) => a - b);
    const labelFor = (index) =>
      index === -1 ? "Autenticação" : pages?.[index] ?? `Secção ${index}`;

    return (
      <div
        style={{ maxHeight: "190px", overflowY: "auto" }}
        className="border rounded p-2 bg-light"
      >
        {all.map((index) => {
          const info = sectionStatus(index);
          const label = labelFor(index);
          const isErrorOnThis =
            errorInfo &&
            (index === currentSection || label === (errorInfo.error || ""));

          return (
            <div
              key={index}
              className="d-flex align-items-center justify-content-between gap-2 mb-2"
            >
              <div
                className="text-truncate"
                title={label}
                style={{ maxWidth: 260 }}
              >
                {label}
              </div>
              <div className="d-flex align-items-center gap-2">
                {isErrorOnThis && (
                  <CBadge color="danger" className="text-white">
                    Erro
                  </CBadge>
                )}
                <CBadge color={info.color}>{info.label}</CBadge>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!enabled) {
    return null;
  }

  const inputsDisabled = loading || awaitingContinue;
  const allSelected = pageCount > 0 && plannedSections.length === pageCount;
  const canSubmit =
    !inputsDisabled &&
    username.trim() &&
    password.trim() &&
    code.trim() &&
    plannedSections.length > 0;
  const showAsyncProgress =
    mode === "async" &&
    (loading ||
      status === "PENDING" ||
      status === "COMPLETE" ||
      status === "ERROR" ||
      serverSectionsRef.current.length > 0);

  return (
    <div className={className}>
      <CDropdown
        visible={visible}
        onShow={() => setVisible(true)}
        onHide={() => setVisible(false)}
        autoClose="outside"
      >
        <CDropdownToggle
          color={loading ? "primary" : "warning"}
          className="submit-dropdown d-inline-flex align-items-center w-auto h-100"
          onClick={toggleVisibility}
          style={{ "--cui-btn-toggle": "none" }}
        >
          {loading ? (
            <CSpinner
              component="span"
              aria-hidden="true"
              className="me-2"
              size="sm"
            />
          ) : (
            <CIcon icon={cilSend} className="me-2" size="lg" />
          )}
          {loading ? (mode === "async" ? "A processar" : "A submeter") : "Submeter"}
        </CDropdownToggle>

        <CDropdownMenu
          className="p-3 shadow border"
          placement="bottom-start"
          style={{ width: 480, zIndex: 1000 }}
        >
          <CCardBody className="p-0">
            <div className="d-flex align-items-start justify-content-between mb-2">
              <h5 className="mb-0">Submeter Candidatura</h5>
            </div>

            {errorInfo && (
              <div className="alert alert-danger py-2" role="alert">
                <strong>Falha:</strong> {errorInfo.error || "Erro desconhecido"}
                {errorInfo.type ? ` (${errorInfo.type})` : null}
              </div>
            )}

            <CForm>
              <div className="mb-2">
                <CFormLabel htmlFor="username">Username</CFormLabel>
                <CFormInput
                  type="text"
                  id="username"
                  placeholder="Introduzir o username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  disabled={inputsDisabled}
                />
              </div>

              <div className="mb-2">
                <div className="d-flex pa-0">
                  <CFormLabel htmlFor="password" className="me-auto">
                    Password
                  </CFormLabel>
                  <CFormCheck
                    id="gov-access"
                    label="Acesso GOV"
                    checked={gov}
                    onChange={(event) => setGov(event.target.checked)}
                    disabled={inputsDisabled}
                  />
                </div>
                <CFormInput
                  type="text"
                  id="password"
                  placeholder="Introduzir a password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={inputsDisabled}
                />
              </div>

              <div className="mb-3">
                <CFormLabel htmlFor="ref">Refª Candidatura</CFormLabel>
                <CFormInput
                  type="text"
                  id="ref"
                  placeholder="Introduzir a referência da candidatura"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  disabled={inputsDisabled}
                />
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <CFormLabel className="mb-0">Secções</CFormLabel>
                  <CButton
                    type="button"
                    size="sm"
                    color={allSelected ? "secondary" : "primary"}
                    variant="outline"
                    onClick={toggleAllSections}
                    disabled={inputsDisabled || !pageCount}
                  >
                    {allSelected ? "Limpar" : "Selecionar tudo"}
                  </CButton>
                </div>

                <div
                  ref={sectionsListRef}
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                  className="border rounded p-2"
                >
                  {pages.map((page, index) => (
                    <CFormCheck
                      key={`${page}-${index}`}
                      id={`section-${index}`}
                      label={page}
                      checked={plannedSections.includes(index)}
                      onChange={() => toggleSection(index)}
                      disabled={inputsDisabled}
                      className="mb-2"
                    />
                  ))}
                </div>
              </div>
            </CForm>

            {showAsyncProgress && (
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <strong>Estado</strong>
                  <small className="text-body-secondary">
                    {progressInfo.total
                      ? `${progressInfo.done}/${progressInfo.total} secções concluídas`
                      : "A iniciar"}
                  </small>
                </div>
                {renderSections()}
              </div>
            )}

            <div className="d-flex gap-2 justify-content-end">
              {mode === "async" && (loading || awaitingContinue || taskId) && (
                <CButton
                  type="button"
                  color="secondary"
                  variant="outline"
                  onClick={handleCancelTask}
                  disabled={awaitingContinue}
                >
                  <CIcon icon={cilX} className="me-2" />
                  Cancelar
                </CButton>
              )}

              {mode === "async" && awaitingContinue && (
                <CButton type="button" color="primary" onClick={handleContinue}>
                  <CIcon icon={cilCaretRight} className="me-2" />
                  Continuar
                </CButton>
              )}

              {!awaitingContinue && (
                <CButton
                  type="button"
                  color="warning"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  {loading ? (
                    <CSpinner size="sm" className="me-2" />
                  ) : (
                    <CIcon
                      icon={mode === "async" ? cilSend : cilCheck}
                      className="me-2"
                    />
                  )}
                  {mode === "async" ? "Submeter" : "Atualizar SIGA"}
                </CButton>
              )}
            </div>
          </CCardBody>
        </CDropdownMenu>
      </CDropdown>
      <style>{`
        .submit-dropdown::after {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

export default SubmitApplication;
